import { forwardRef } from 'react';
import type { AgentIdentity, AvatarColour, UserRef } from '@slate/shared';
import { avatarColourFor } from '@slate/shared';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn, initials } from '@/lib/utils';

const Avatar = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn('relative flex size-8 shrink-0 overflow-hidden rounded-full', className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

/**
 * The twelve palette colours, in light and dark — card #53.
 *
 * Written out rather than built from a template, because Tailwind reads the
 * source for class names and `bg-${colour}-600` is a string it cannot see.
 *
 * A solid fill with white initials, not a tint: an avatar is 24px in a feed,
 * and at that size a 15%-opacity wash is the same pale disc for all twelve.
 * The dark step is one shade lighter so it does not sink into the background
 * the way a 600 does on a near-black page.
 */
const COLOUR_CLASSES: Record<AvatarColour, string> = {
  slate: 'bg-slate-500 dark:bg-slate-400 dark:text-slate-950',
  red: 'bg-red-600 dark:bg-red-500',
  orange: 'bg-orange-600 dark:bg-orange-500',
  amber: 'bg-amber-600 dark:bg-amber-500 dark:text-amber-950',
  green: 'bg-green-600 dark:bg-green-500 dark:text-green-950',
  teal: 'bg-teal-600 dark:bg-teal-500 dark:text-teal-950',
  sky: 'bg-sky-600 dark:bg-sky-500 dark:text-sky-950',
  blue: 'bg-blue-600 dark:bg-blue-500',
  violet: 'bg-violet-600 dark:bg-violet-500',
  fuchsia: 'bg-fuchsia-600 dark:bg-fuchsia-500',
  pink: 'bg-pink-600 dark:bg-pink-500',
  rose: 'bg-rose-600 dark:bg-rose-500',
};

/** The classes for a person's avatar ground, chosen or derived. Exported for the picker. */
export function avatarColourClasses(colour: AvatarColour): string {
  return cn('text-white', COLOUR_CLASSES[colour]);
}

/** What `UserAvatar` needs to draw someone. Anything shaped like a `UserRef` fits. */
export type AvatarUser = Pick<UserRef, 'id' | 'name' | 'avatarUrl' | 'avatarColor'> & {
  isActive?: boolean;
};

/**
 * A person, everywhere they appear. Deactivated users are rendered at reduced
 * opacity rather than hidden — §9.3 keeps their attribution intact, so the UI
 * has to show them without implying they are still active.
 *
 * #53 — it takes the person rather than three of their fields. The colour
 * behind the initials is derived from the id when none is chosen, so the id
 * has to travel with the name; passing them separately meant every one of
 * twenty call sites could quietly forget one and nothing would say so.
 */
export function UserAvatar({
  user,
  className,
  title,
}: {
  user: AvatarUser;
  className?: string;
  /**
   * #50 — what the hover says, when the name alone does not say enough. A list
   * row carries two of these and they are not interchangeable: "Assignee: Brad
   * Gibby" and "Created by Brad Gibby" are the same face and different facts.
   */
  title?: string;
}): JSX.Element {
  const colour = avatarColourFor(user.id, user.avatarColor);
  const active = user.isActive ?? true;
  return (
    <Avatar className={cn(!active && 'opacity-50 grayscale', className)} title={title ?? user.name}>
      {user.avatarUrl ? <AvatarPrimitive.Image src={user.avatarUrl} alt="" /> : null}
      <AvatarPrimitive.Fallback
        className={cn(
          'flex size-full items-center justify-center text-[0.7rem] font-semibold',
          avatarColourClasses(colour),
        )}
      >
        {initials(user.name)}
      </AvatarPrimitive.Fallback>
    </Avatar>
  );
}

/**
 * An agent, wearing its own face — card #37.
 *
 * Square-ish rounding rather than a circle, deliberately: at 24px a glance
 * down the feed should separate "a person said this" from "an agent did
 * this" before any name is read. An unrecognised client has no asset and
 * falls back to its initials like anyone else.
 */
export function AgentAvatar({
  identity,
  className,
}: {
  identity: AgentIdentity;
  className?: string;
}): JSX.Element {
  return (
    <Avatar className={cn('rounded-md', className)} title={identity.name}>
      {identity.avatarUrl ? <AvatarPrimitive.Image src={identity.avatarUrl} alt="" /> : null}
      <AvatarPrimitive.Fallback className="bg-secondary text-secondary-foreground flex size-full items-center justify-center text-[0.7rem] font-semibold">
        {initials(identity.name)}
      </AvatarPrimitive.Fallback>
    </Avatar>
  );
}

export { Avatar };
